import PropTypes from 'prop-types';
import React from 'react';
import {
  Breadcrumb, BreadcrumbItem, ButtonGroup, ButtonToolbar,
} from 'reactstrap';

/**
 * Skeleton placeholder for the PropertyList component.
 *
 * By default mirrors the full page layout — breadcrumb, filter bar, and
 * 4-column property card grid — shown while data loads or when a field error
 * is caught by PlaceholderErrorBoundary.
 *
 * When `minimal` is true the breadcrumb and filter-bar skeletons are omitted,
 * leaving only the card grid. Use this variant behind PlaceholderErrorModal
 * during a pagination error, where the real breadcrumb and filter bar are
 * already visible on screen and duplicating them would cause layout shift.
 * @param {object} props - Component props.
 * @param {boolean} props.minimal - When true, renders only the card grid skeleton.
 * @returns {React.ReactElement} PropertyListPlaceholder component
 */
function PropertyListPlaceholder({ minimal = false }) {
  return (
    <div aria-hidden="true">
      {!minimal && (
        <>
          {/* Breadcrumbs skeleton: matches the single-item "Properties" breadcrumb */}
          <Breadcrumb>
            <BreadcrumbItem>
              <span className="placeholder placeholder-glow bg-darken" style={{ width: '4.5rem' }} />
            </BreadcrumbItem>
          </Breadcrumb>

          {/*
           * FilterBar skeleton: mirrors the ButtonToolbar rendered by FilterBar.
           * Group 1 → 1 button ("All"), Group 2 → 2 buttons ("Active" | "Inactive").
           */}
          <ButtonToolbar className="me-2">
            <ButtonGroup className="me-2 mb-2">
              <span className="placeholder placeholder-glow bg-darken btn btn-sm" style={{ width: '2.25rem' }} />
            </ButtonGroup>
            <ButtonGroup className="me-2 mb-2">
              <span className="placeholder placeholder-glow bg-darken btn btn-sm" style={{ width: '3.25rem' }} />
              <span className="placeholder placeholder-glow bg-darken btn btn-sm" style={{ width: '4.25rem' }} />
            </ButtonGroup>
          </ButtonToolbar>
        </>
      )}

      {/* Property card grid skeleton */}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 mt-2">
        {[...Array(8)].map((_, index) => (
          <div className="col mb-4" key={`placeholder-card-${index + 1}`}>
            <div className="card h-100">
              <span
                className="placeholder placeholder-glow bg-darken"
                style={{ height: '8rem', display: 'block' }}
              />
              <div className="card-body">
                <div className="mb-2">
                  <span className="placeholder placeholder-glow bg-darken col-8" />
                </div>
                <div>
                  <span className="placeholder placeholder-glow bg-darken placeholder-sm col-10" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

PropertyListPlaceholder.propTypes = {
  minimal: PropTypes.bool,
};

export default PropertyListPlaceholder;
